// Contact A-Z Listing Component
class ContactAZListing {
    constructor(container) {
        this.container = container;
        this.data = [];
        this.filteredData = [];
        this.currentFilters = {
            search: "",
            category: "",
            courtType: "",
        };

        this.init();
    }

    init() {
        this.loadDataFromDOM();
        this.setupEventListeners();
        this.generateAlphabetFilter();
        this.populateCourtTypeFilter();
        // Note: Initial rendering is handled by the Handlebars helper
    }

    loadDataFromDOM() {
        try {
            const courtItems = this.container.querySelectorAll(
                ".qld__contact_a_z__court-item"
            );
            this.data = Array.from(courtItems).map((item) =>
                this.extractCourtData(item)
            );

            if (this.data.length === 0) {
                console.warn("No contact A-Z data found in DOM");
            }
        } catch (error) {
            console.error("Error loading contact A-Z data from DOM:", error);
            this.data = [];
        }
    }

    extractCourtData(item) {
        // Get data from individual data attributes
        const name =
            item.getAttribute("data-court-name") ||
            item.querySelector(".qld__contact_a_z__court-name")?.textContent ||
            "";

        const category =
            item.getAttribute("data-court-category") ||
            this.getCategoryFromParent(item);

        const courtType = this.extractCourtTypes(item);

        // Extract link from the court name element
        const courtNameElement = item.querySelector(
            ".qld__contact_a_z__court-name"
        );
        const linkElement = courtNameElement?.querySelector("a");
        const link = linkElement ? linkElement.getAttribute("href") : "";

        // Extract addresses (they're stored as JSON strings)
        let courtAddress = null;
        let registryAddress = null;

        const courtAddressAttr = item.getAttribute("data-court-address");
        if (courtAddressAttr) {
            try {
                courtAddress = JSON.parse(decodeURIComponent(courtAddressAttr));
            } catch (error) {
                console.error("Error parsing court address:", error);
            }
        }

        const registryAddressAttr = item.getAttribute("data-registry-address");
        if (registryAddressAttr) {
            try {
                registryAddress = JSON.parse(
                    decodeURIComponent(registryAddressAttr)
                );
            } catch (error) {
                console.error("Error parsing registry address:", error);
            }
        }

        return {
            name,
            category,
            courtType,
            link,
            courtAddress,
            courtPhone: item.getAttribute("data-court-phone"),
            courtEmail: item.getAttribute("data-court-email"),
            courtHours: item.getAttribute("data-court-hours"),
            courtOtherLabel: item.getAttribute("data-court-other-label"),
            courtOtherValue: item.getAttribute("data-court-other-value"),
            registryAddress,
            registryPhone: item.getAttribute("data-registry-phone"),
            registryEmail: item.getAttribute("data-registry-email"),
            registryHours: item.getAttribute("data-registry-hours"),
            registryOtherLabel: item.getAttribute("data-registry-other-label"),
            registryOtherValue: item.getAttribute("data-registry-other-value"), 
        };
    }

    extractCourtTypes(item) {
        const courtTypeData = item.getAttribute("data-court-types");
        return courtTypeData ? courtTypeData.split(",") : [];
    }

    getCategoryFromParent(courtItem) {
        // Find the category section that contains this court item
        const categorySection = courtItem.closest(
            ".qld__contact_a_z__category-section"
        );
        const categoryTitle = categorySection?.querySelector(
            ".qld__contact_a_z__category-title"
        );
        return categoryTitle?.textContent || "A";
    }

    setupEventListeners() {
        // Search functionality
        const searchInput = this.container.querySelector("#searchInput");
        const searchBtn = this.container.querySelector("#searchBtn");

        if (searchInput) {
            searchInput.addEventListener("input", (e) => {
                this.currentFilters.search = e.target.value.toLowerCase();
                this.applyFilters();
            });

            searchInput.addEventListener("keypress", (e) => {
                if (e.key === "Enter") {
                    this.applyFilters();
                }
            });
        }

        if (searchBtn) {
            searchBtn.addEventListener("click", () => {
                this.applyFilters();
            });
        }

        // Court type filter is now handled in setupCustomSelect()

        // Setup accordion functionality for existing items
        this.setupAccordion();
    }

    generateAlphabetFilter() {
        const alphabetFilter = this.container.querySelector("#alphabetFilter");
        if (!alphabetFilter) return;

        const availableCategories = [
            ...new Set(this.data.map((item) => item.category)),
        ];

        // Generate A-Z buttons
        for (let i = 65; i <= 90; i++) {
            const letter = String.fromCharCode(i);
            const button = document.createElement("button");
            button.className = "qld__contact_a_z__letter-btn";
            button.textContent = letter;

            // Disable buttons for categories that don't exist
            if (!availableCategories.includes(letter)) {
                button.classList.add("qld__contact_a_z__letter-btn--disabled");
            }

            button.addEventListener("click", () => {
                if (
                    !button.classList.contains(
                        "qld__contact_a_z__letter-btn--disabled"
                    )
                ) {
                    this.toggleCategoryFilter(letter, button);
                }
            });

            alphabetFilter.appendChild(button);
        }
    }

    toggleCategoryFilter(category, button) {
        const allButtons = this.container.querySelectorAll(
            ".qld__contact_a_z__letter-btn"
        );

        // Remove active class from all buttons
        allButtons.forEach((btn) =>
            btn.classList.remove("qld__contact_a_z__letter-btn--active")
        );

        // If clicking the same category, clear the filter
        if (this.currentFilters.category === category) {
            this.currentFilters.category = "";
        } else {
            // Set new category filter
            this.currentFilters.category = category;
            button.classList.add("qld__contact_a_z__letter-btn--active");
        }

        this.applyFilters();
    }

    clearAlphabetFilter() {
        // Clear the category filter
        this.currentFilters.category = "";

        // Remove active class from all letter buttons
        const allButtons = this.container.querySelectorAll(
            ".qld__contact_a_z__letter-btn"
        );
        allButtons.forEach((btn) =>
            btn.classList.remove("qld__contact_a_z__letter-btn--active")
        );
    }

    updateAlphabetFilter() {
        const alphabetFilter = this.container.querySelector("#alphabetFilter");
        if (!alphabetFilter) return;

        // Get available categories from original data
        const originalAvailableCategories = [
            ...new Set(this.data.map((item) => item.category)),
        ];

        // Check if any filters are applied (excluding category filter)
        const hasOtherFilters =
            this.currentFilters.search || this.currentFilters.courtType;

        // Determine which data to check for available letters
        let dataToCheck;
        if (!hasOtherFilters && !this.currentFilters.category) {
            // No filters applied - use original data
            dataToCheck = this.data;
        } else {
            // Filters are applied - get filtered data without category filter
            // This shows which letters would be available if we removed the letter filter
            const tempFilters = { ...this.currentFilters };
            tempFilters.category = ""; // Temporarily remove category filter

            dataToCheck = this.data.filter((item) => {
                // Search filter
                if (
                    tempFilters.search &&
                    !this.searchInCourtDetails(item, tempFilters.search)
                ) {
                    return false;
                }

                // Court type filter
                if (
                    tempFilters.courtType &&
                    (!item.courtType ||
                        !item.courtType.includes(tempFilters.courtType))
                ) {
                    return false;
                }

                return true;
            });
        }

        const availableCategories = [
            ...new Set(dataToCheck.map((item) => item.category)),
        ];

        // Update each letter button's disabled state
        const allButtons = this.container.querySelectorAll(
            ".qld__contact_a_z__letter-btn"
        );
        allButtons.forEach((button) => {
            const letter = button.textContent.trim();
            const existsInOriginal = originalAvailableCategories.includes(letter);
            const isAvailable = availableCategories.includes(letter);

            // Disable if it doesn't exist in original data OR if it's not available in the data we're checking
            if (!existsInOriginal || !isAvailable) {
                button.classList.add("qld__contact_a_z__letter-btn--disabled");
            } else {
                button.classList.remove("qld__contact_a_z__letter-btn--disabled");
            }
        });
    }

    populateCourtTypeFilter() {
        const dropdown = this.container.querySelector(
            ".qld__contact_a_z__select-dropdown"
        );
        if (!dropdown) return;

        const allCourtTypes = new Set();

        // Collect all unique court types
        this.data.forEach((item) => {
            if (Array.isArray(item.courtType)) {
                item.courtType.forEach((type) => {
                    if (type && type.trim()) {
                        allCourtTypes.add(type.trim());
                    }
                });
            }
        });

        // Sort and add options
        const sortedCourtTypes = Array.from(allCourtTypes).sort();
        sortedCourtTypes.forEach((type) => {
            const option = document.createElement("div");
            option.className = "qld__contact_a_z__select-option";
            option.setAttribute("data-value", type);
            option.setAttribute("role", "option");
            option.setAttribute("tabindex", "-1");
            option.textContent = this.formatCourtTypeName(type);
            dropdown.appendChild(option);
        });

        this.setupCustomSelect();
    }

    setupCustomSelect() {
        const trigger = this.container.querySelector(
            ".qld__contact_a_z__select-trigger"
        );
        const dropdown = this.container.querySelector(
            ".qld__contact_a_z__select-dropdown"
        );
        const selectText = this.container.querySelector(
            ".qld__contact_a_z__select-text"
        );

        if (!trigger || !dropdown || !selectText) return;

        // Toggle dropdown on trigger click
        trigger.addEventListener("click", (e) => {
            e.stopPropagation();
            this.toggleCustomDropdown(trigger, dropdown);
        });

        // Handle option selection
        dropdown.addEventListener("click", (e) => {
            const option = e.target.closest(".qld__contact_a_z__select-option");
            if (option) {
                const value = option.getAttribute("data-value");
                const text = option.textContent;

                // Update trigger text
                selectText.textContent = text;

                // Update current filter
                this.currentFilters.courtType = value;

                // Clear A-Z filter when court type is selected
                this.clearAlphabetFilter();

                // Close dropdown
                this.closeCustomDropdown(trigger, dropdown);

                // Apply filters
                this.applyFilters();
            }
        });

        // Close dropdown when clicking outside
        document.addEventListener("click", (e) => {
            if (!trigger.contains(e.target) && !dropdown.contains(e.target)) {
                this.closeCustomDropdown(trigger, dropdown);
            }
        });

        // Handle keyboard navigation
        trigger.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.toggleCustomDropdown(trigger, dropdown);
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                this.toggleCustomDropdown(trigger, dropdown);
            }
        });

        // Handle keyboard navigation when dropdown is open
        dropdown.addEventListener("keydown", (e) => {
            const options = dropdown.querySelectorAll(
                ".qld__contact_a_z__select-option"
            );
            const currentIndex = Array.from(options).findIndex((option) =>
                option.classList.contains(
                    "qld__contact_a_z__select-option--selected"
                )
            );

            switch (e.key) {
                case "ArrowDown":
                    e.preventDefault();
                    e.stopPropagation();
                    this.navigateDropdown(options, currentIndex, 1);
                    break;
                case "ArrowUp":
                    e.preventDefault();
                    e.stopPropagation();
                    this.navigateDropdown(options, currentIndex, -1);
                    break;
                case "Enter":
                    e.preventDefault();
                    e.stopPropagation();
                    const selectedOption = dropdown.querySelector(
                        ".qld__contact_a_z__select-option--selected"
                    );
                    if (selectedOption) {
                        selectedOption.click();
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    e.stopPropagation();
                    this.closeCustomDropdown(trigger, dropdown);
                    trigger.focus(); // Return focus to trigger
                    break;
                case "Tab":
                    // Allow normal tab navigation but close dropdown
                    this.closeCustomDropdown(trigger, dropdown);
                    break;
            }
        });
    }

    toggleCustomDropdown(trigger, dropdown) {
        const isOpen = dropdown.classList.contains(
            "qld__contact_a_z__select-dropdown--open"
        );

        if (isOpen) {
            this.closeCustomDropdown(trigger, dropdown);
        } else {
            this.openCustomDropdown(trigger, dropdown);
        }
    }

    openCustomDropdown(trigger, dropdown) {
        trigger.classList.add("qld__contact_a_z__select-trigger--open");
        dropdown.classList.add("qld__contact_a_z__select-dropdown--open");

        // Update ARIA attributes
        trigger.setAttribute("aria-expanded", "true");

        // Focus first option and make it focusable
        const firstOption = dropdown.querySelector(
            ".qld__contact_a_z__select-option"
        );
        if (firstOption) {
            firstOption.classList.add(
                "qld__contact_a_z__select-option--selected"
            );
            firstOption.setAttribute("aria-selected", "true");
            firstOption.setAttribute("tabindex", "0");
            firstOption.focus();
        }
    }

    closeCustomDropdown(trigger, dropdown) {
        trigger.classList.remove("qld__contact_a_z__select-trigger--open");
        dropdown.classList.remove("qld__contact_a_z__select-dropdown--open");

        // Update ARIA attributes
        trigger.setAttribute("aria-expanded", "false");

        // Remove selection highlight and reset tabindex
        const selectedOption = dropdown.querySelector(
            ".qld__contact_a_z__select-option--selected"
        );
        if (selectedOption) {
            selectedOption.classList.remove(
                "qld__contact_a_z__select-option--selected"
            );
            selectedOption.setAttribute("aria-selected", "false");
            selectedOption.setAttribute("tabindex", "-1");
        }

        // Reset all options tabindex
        const allOptions = dropdown.querySelectorAll(
            ".qld__contact_a_z__select-option"
        );
        allOptions.forEach((option) => {
            option.setAttribute("tabindex", "-1");
        });
    }

    navigateDropdown(options, currentIndex, direction) {
        const newIndex = currentIndex + direction;

        if (newIndex >= 0 && newIndex < options.length) {
            // Remove current selection
            if (currentIndex >= 0 && currentIndex < options.length) {
                options[currentIndex].classList.remove(
                    "qld__contact_a_z__select-option--selected"
                );
                options[currentIndex].setAttribute("aria-selected", "false");
                options[currentIndex].setAttribute("tabindex", "-1");
            }

            // Add new selection
            options[newIndex].classList.add(
                "qld__contact_a_z__select-option--selected"
            );
            options[newIndex].setAttribute("aria-selected", "true");
            options[newIndex].setAttribute("tabindex", "0");
            options[newIndex].focus();

            // Scroll into view if needed
            options[newIndex].scrollIntoView({ block: "nearest" });
        }
    }

    formatCourtTypeName(type) {
        return type
            .split("-")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }

    applyFilters() {
        this.filteredData = this.data.filter((item) => {
            // Search filter
            if (
                this.currentFilters.search &&
                !this.searchInCourtDetails(item, this.currentFilters.search)
            ) {
                return false;
            }

            // Category filter
            if (
                this.currentFilters.category &&
                item.category !== this.currentFilters.category
            ) {
                return false;
            }

            // Court type filter
            if (
                this.currentFilters.courtType &&
                (!item.courtType ||
                    !item.courtType.includes(this.currentFilters.courtType))
            ) {
                return false;
            }

            return true;
        });

        this.updateAlphabetFilter();
        this.renderResults();
    }

    searchInCourtDetails(court, searchTerm) {
        const searchLower = searchTerm.toLowerCase();

        // Search in court name
        if (court.name && court.name.toLowerCase().includes(searchLower)) {
            return true;
        }

        // Search in court address
        if (court.courtAddress) {
            if (Array.isArray(court.courtAddress)) {
                for (let i = 0; i < court.courtAddress.length; i++) {
                    if (
                        court.courtAddress[i] &&
                        court.courtAddress[i]
                            .toLowerCase()
                            .includes(searchLower)
                    ) {
                        return true;
                    }
                }
            } else if (court.courtAddress.toLowerCase().includes(searchLower)) {
                return true;
            }
        }

        // Search in court phone
        if (
            court.courtPhone &&
            court.courtPhone.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in court hours
        if (
            court.courtHours &&
            court.courtHours.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in court email
        if (
            court.courtEmail &&
            court.courtEmail.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in court other label
        if (
            court.courtOtherLabel &&
            court.courtOtherLabel.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in court other value
        if (
            court.courtOtherValue &&
            court.courtOtherValue.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in registry address
        if (court.registryAddress) {
            if (Array.isArray(court.registryAddress)) {
                for (let i = 0; i < court.registryAddress.length; i++) {
                    if (
                        court.registryAddress[i] &&
                        court.registryAddress[i]
                            .toLowerCase()
                            .includes(searchLower)
                    ) {
                        return true;
                    }
                }
            } else if (
                court.registryAddress.toLowerCase().includes(searchLower)
            ) {
                return true;
            }
        }

        // Search in registry phone
        if (
            court.registryPhone &&
            court.registryPhone.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in registry email
        if (
            court.registryEmail &&
            court.registryEmail.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in registry hours
        if (
            court.registryHours &&
            court.registryHours.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in registry other label
        if (
            court.registryOtherLabel &&
            court.registryOtherLabel.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        // Search in registry other value
        if (
            court.registryOtherValue &&
            court.registryOtherValue.toLowerCase().includes(searchLower)
        ) {
            return true;
        }

        return false;
    }

    renderResults() {
        const resultsContainer =
            this.container.querySelector("#resultsContainer");
        if (!resultsContainer) return;

        if (this.filteredData.length === 0) {
            resultsContainer.innerHTML = `
                <div class="qld__contact_a_z__no-results">
                    <i class="fas fa-search"></i>
                    <h3>No courts found</h3>
                    <p>Try adjusting your search criteria or filters.</p>
                </div>
            `;
            return;
        }

        // Group by category
        const groupedData = this.groupByCategory(this.filteredData);

        let html = "";

        Object.keys(groupedData)
            .forEach((category) => {
                html += `
                <div class="qld__contact_a_z__category-section">
                    <h2 class="qld__contact_a_z__category-title">${category}</h2>
            `;

                groupedData[category].forEach((court) => {
                    html += this.renderCourtItem(court);
                });

                html += "</div>";
            });

        resultsContainer.innerHTML = html;

        // Setup accordion functionality for new items
        this.setupAccordion();
    }

    groupByCategory(data) {
        return data.reduce((groups, item) => {
            const category = item.category;
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(item);
            return groups;
        }, {});
    }

    renderCourtItem(court) {
        const dataAttributes = this.buildDataAttributes(court);
        const header = this.buildCourtHeader(court);
        const courtSection = this.renderLocationSection(
            court,
            "Court location",
            "court"
        );
        const registrySection = this.renderLocationSection(
            court,
            "Registry location (enquiries and filing)",
            "registry"
        );

        return `
            <div class="qld__contact_a_z__court-item"${dataAttributes}>
                ${header}
                <div class="qld__contact_a_z__court-content">
                    <div class="qld__contact_a_z__court-details">
                        ${courtSection}
                        ${registrySection}
                    </div>
                </div>
            </div>
        `;
    }

    buildDataAttributes(court) {
        const attributes = [];

        if (court.courtType && court.courtType.length > 0) {
            attributes.push(`data-court-types="${court.courtType.join(",")}"`);
        }

        // Store individual data attributes
        if (court.name) {
            attributes.push(`data-court-name="${court.name}"`);
        }
        if (court.category) {
            attributes.push(`data-court-category="${court.category}"`);
        }
        if (court.courtAddress) {
            attributes.push(
                `data-court-address="${encodeURIComponent(
                    JSON.stringify(court.courtAddress)
                )}"`
            );
        }
        if (court.courtPhone) {
            attributes.push(`data-court-phone="${court.courtPhone}"`);
        }
        if (court.courtEmail) {
            attributes.push(`data-court-email="${court.courtEmail}"`);
        }
        if (court.courtHours) {
            attributes.push(`data-court-hours="${court.courtHours}"`);
        }

        if (court.courtOtherLabel) {
            attributes.push(`data-court-other-label="${court.courtOtherLabel}"`);
        }
        if (court.courtOtherValue) {
            attributes.push(`data-court-other-value="${court.courtOtherValue}"`);
        }

        if (court.registryAddress) {
            attributes.push(
                `data-registry-address="${encodeURIComponent(
                    JSON.stringify(court.registryAddress)
                )}"`
            );
        }
        if (court.registryPhone) {
            attributes.push(`data-registry-phone="${court.registryPhone}"`);
        }
        if (court.registryEmail) {
            attributes.push(`data-registry-email="${court.registryEmail}"`);
        }
        if (court.registryHours) {
            attributes.push(`data-registry-hours="${court.registryHours}"`);
        }

        if (court.registryOtherLabel) {
            attributes.push(`data-registry-other-label="${court.registryOtherLabel}"`);
        }
        if (court.registryOtherValue) {
            attributes.push(`data-registry-other-value="${court.registryOtherValue}"`);
        }

        return attributes.length > 0 ? " " + attributes.join(" ") : "";
    }

    buildCourtHeader(court) {
        return `
            <div class="qld__contact_a_z__court-header">
                <span class="qld__contact_a_z__court-name">${court.name}</span>
                <i class="fas fa-chevron-down qld__contact_a_z__court-toggle"></i>
            </div>
        `;
    }

    renderLocationSection(court, title, type) {
        const address = court[`${type}Address`];
        if (!address || !this.renderAddress(address)) return "";

        const phone = court[`${type}Phone`];
        const email = court[`${type}Email`];
        const hours = court[`${type}Hours`];
        const otherLabel = court[`${type}OtherLabel`];
        const otherValue = court[`${type}OtherValue`];

        return `
            <div class="qld__contact_a_z__detail-section">
                <div class="qld__contact_a_z__detail-title">${title}</div>
                <div class="qld__contact_a_z__detail-content">
                    ${this.renderAddressRow("Address:", address)}
                    ${phone ? this.renderPhoneRow("Phone:", phone) : ""}
                    ${
                        hours
                            ? this.renderDetailRow("Business hours:", hours)
                            : ""
                    }
                    ${email ? this.renderEmailRow("Email:", email) : ""}

                    ${otherLabel ? this.renderDetailRow(otherLabel + ":", otherValue) : ""}
                </div>
            </div>
        `;
    }

    renderAddressRow(label, address) {
        const mapLink = address ? this.renderMapLink(address) : "";
        return `
            <div class="qld__contact_a_z__detail-row">
                <div class="qld__contact_a_z__detail-label">${label}</div>
                <div class="qld__contact_a_z__detail-value">
                    ${this.renderAddress(address)}
                    ${mapLink}
                </div>
            </div>
        `;
    }

    renderDetailRow(label, value) {
        return `
            <div class="qld__contact_a_z__detail-row">
                <div class="qld__contact_a_z__detail-label">${label}</div>
                <div class="qld__contact_a_z__detail-value">${value}</div>
            </div>
        `;
    }
    
    renderPhoneRow(label, phone) {
        return `
            <div class="qld__contact_a_z__detail-row">
                <div class="qld__contact_a_z__detail-label">${label}</div>
                <div class="qld__contact_a_z__detail-value">
                    <a href="tel:${phone}">${phone}</a>
                </div>
            </div>
        `;
    }

    renderEmailRow(label, email) {
        return `
            <div class="qld__contact_a_z__detail-row">
                <div class="qld__contact_a_z__detail-label">${label}</div>
                <div class="qld__contact_a_z__detail-value">
                    <a href="mailto:${email}">${email}</a>
                </div>
            </div>
        `;
    }

    renderMapLink(address) {
        const addressQuery = Array.isArray(address)
            ? address.join(", ")
            : address;
        return `<div class="qld__contact_a_z__map-link"><a href="https://maps.google.com/?q=${encodeURIComponent(
            addressQuery
        )}" target="_blank">Map</a></div>`;
    }

    renderAddress(address) {
        if (Array.isArray(address)) {
            return address
                .filter((line) => line.trim())
                .map((line) => `<div>${line}</div>`)
                .join("");
        } else if (typeof address === "string" && address.trim()) {
            return `<div>${address}</div>`;
        }
        return "";
    }

    setupAccordion() {
        // Setup accordion functionality for all court headers
        const courtHeaders = this.container.querySelectorAll(
            ".qld__contact_a_z__court-header"
        );
        courtHeaders.forEach((header) => {
            // Remove existing click handlers to prevent duplicates
            header.removeEventListener("click", this.handleAccordionClick);
            // Add new click handler
            header.addEventListener(
                "click",
                this.handleAccordionClick.bind(this)
            );
        });
    }

    handleAccordionClick(event) {
        this.toggleAccordion(event.currentTarget);
    }

    toggleAccordion(header) {
        const content = header.nextElementSibling;
        const toggle = header.querySelector(".qld__contact_a_z__court-toggle");

        // Close all other accordions
        const allHeaders = this.container.querySelectorAll(
            ".qld__contact_a_z__court-header"
        );
        const allContents = this.container.querySelectorAll(
            ".qld__contact_a_z__court-content"
        );
        const allToggles = this.container.querySelectorAll(
            ".qld__contact_a_z__court-toggle"
        );

        allHeaders.forEach((h, index) => {
            if (h !== header) {
                allContents[index].classList.remove(
                    "qld__contact_a_z__court-content--expanded"
                );
                allToggles[index].classList.remove(
                    "qld__contact_a_z__court-toggle--expanded"
                );
            }
        });

        // Toggle current accordion
        content.classList.toggle("qld__contact_a_z__court-content--expanded");
        toggle.classList.toggle("qld__contact_a_z__court-toggle--expanded");
    }
}

// Initialize the component when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
    const contactAZContainers = document.querySelectorAll(".qld__contact_a_z");

    contactAZContainers.forEach((container) => {
        // The HTML is already rendered by Handlebars, just enhance with interactivity
        const contactAZListing = new ContactAZListing(container);

        // Store reference for accordion functionality
        container.contactAZListing = contactAZListing;
    });
});
